import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const API_URL = 'https://airexheater.in/api/admin/auth';
const UPLOAD_URL = 'https://airexheater.in/api/admin';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API_URL}/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
      console.log('Fetched user data from server:', data.user); // Debug log
      console.log('Avatar URL from server:', data.user.avatar); // Debug log
        setUser(data.user);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const signup = async (name, email, password) => {
    const response = await fetch(`${API_URL}/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password })
    });
    
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || data.errors?.[0]?.msg || 'Signup failed');
    }
    
    localStorage.setItem('adminToken', data.token);
    localStorage.removeItem('activeSidebarItem');
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const login = async (email, password) => {
    const response = await fetch(`${API_URL}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Login failed');
    }
    
    localStorage.setItem('adminToken', data.token);
    localStorage.removeItem('activeSidebarItem');
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('activeSidebarItem');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await fetch(`${API_URL}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });
      
      const data = await response.json();
      if (response.ok) {
        setUser(data.user);
        return { success: true, user: data.user };
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const updatePassword = async (currentPassword, newPassword) => {
    try {
      const response = await fetch(`${API_URL}/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });
      
      const data = await response.json();
      if (response.ok) {
        return { success: true };
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const uploadAvatar = async (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    
    try {
      const response = await fetch(`${UPLOAD_URL}/upload/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      if (response.ok) {
        setUser(prev => ({ ...prev, avatar: data.url }));
        return { success: true, url: data.url };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Upload error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const deleteAvatar = async () => {
    try {
      const response = await fetch(`${UPLOAD_URL}/upload/avatar`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (response.ok) {
        setUser(prev => ({ ...prev, avatar: null }));
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Delete avatar error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const hasRole = (role) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return user.role === role;
  };

  const canDelete = () => {
    if (!user) return false;
    return user.role === 'super_admin';
  };

  const canEdit = () => {
    if (!user) return false;
    return user.role === 'super_admin' || user.role === 'admin';
  };

  const canAdd = () => {
    if (!user) return false;
    return true;
  };

  const canView = () => {
    if (!user) return false;
    return true;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      signup, 
      login, 
      logout,
      updateProfile,
      updatePassword,
      uploadAvatar,
      deleteAvatar,
      hasRole,
      canDelete,
      canEdit,
      canAdd,
      canView,
      token 
    }}>
      {children}
    </AuthContext.Provider>
  );
};